import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RevenueChart = ({ data, loading = false }) => {
  const [activeLines, setActiveLines] = useState({
    revenue: true,
    users: true,
    tenants: true
  });

  const toggleLine = (lineKey) => {
    setActiveLines(prev => ({
      ...prev,
      [lineKey]: !prev[lineKey]
    }));
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-elevation-3">
          <p className="text-sm font-medium text-text-primary mb-2">{label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center space-x-2 text-xs">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-text-secondary">{entry.name}:</span>
              <span className="font-medium text-text-primary">{entry.value}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-6">
            <div className="h-6 bg-muted rounded w-32"></div>
            <div className="flex space-x-2">
              <div className="h-8 bg-muted rounded w-20"></div>
              <div className="h-8 bg-muted rounded w-20"></div>
              <div className="h-8 bg-muted rounded w-20"></div>
            </div>
          </div>
          <div className="h-80 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <h2 className="text-lg font-semibold text-text-primary">Platform Performance Trends</h2>
          <div className="flex items-center space-x-1 text-xs text-text-secondary">
            <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
            <span>Live Data</span>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant={activeLines.revenue ? "default" : "outline"}
            size="sm"
            onClick={() => toggleLine('revenue')}
            className="text-xs"
          >
            <div className="w-3 h-3 bg-primary rounded-full mr-2"></div>
            Revenue
          </Button>
          <Button
            variant={activeLines.users ? "default" : "outline"}
            size="sm"
            onClick={() => toggleLine('users')}
            className="text-xs"
          >
            <div className="w-3 h-3 bg-secondary rounded-full mr-2"></div>
            Users
          </Button>
          <Button
            variant={activeLines.tenants ? "default" : "outline"}
            size="sm"
            onClick={() => toggleLine('tenants')}
            className="text-xs"
          >
            <div className="w-3 h-3 bg-accent rounded-full mr-2"></div>
            Tenants
          </Button>
          
          <div className="border-l border-border pl-2 ml-2">
            <Button variant="ghost" size="sm">
              <Icon name="Download" size={16} />
            </Button>
          </div>
        </div>
      </div>
      
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="month" 
              stroke="var(--color-text-secondary)"
              fontSize={12}
            />
            <YAxis 
              stroke="var(--color-text-secondary)"
              fontSize={12}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            
            {activeLines.revenue && (
              <Line
                type="monotone"
                dataKey="revenue"
                stroke="var(--color-primary)"
                strokeWidth={2}
                dot={{ fill: 'var(--color-primary)', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: 'var(--color-primary)', strokeWidth: 2 }}
                name="Revenue ($K)"
              />
            )}
            
            {activeLines.users && (
              <Line
                type="monotone"
                dataKey="users"
                stroke="var(--color-secondary)"
                strokeWidth={2}
                dot={{ fill: 'var(--color-secondary)', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: 'var(--color-secondary)', strokeWidth: 2 }}
                name="Active Users"
              />
            )}
            
            {activeLines.tenants && (
              <Line
                type="monotone"
                dataKey="tenants"
                stroke="var(--color-accent)"
                strokeWidth={2}
                dot={{ fill: 'var(--color-accent)', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: 'var(--color-accent)', strokeWidth: 2 }}
                name="Total Tenants"
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default RevenueChart;